<template>
  <div class="flex1">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <Spinner />
      <p>{{ loadingMessage }}</p>
    </div>

    <!-- Checklist Modal for Starting Experiments -->
    <looker-links-modal
      ref="lookerLinksModal"
      :links="selectedLookerLinks"
    />

    <experiment-start-checklist
      ref="experimentStartChecklist"
      :experiment-name="currentChecklistExperimentId ? experiment(currentChecklistExperimentId).name : ''"
      :ui-images="checklistImages"
      @confirmed="handleChecklistConfirmed"
      @cancel="handleChecklistCancelled"
    />

    <template-looks-modal ref="templateLooksModal" />

    <div class="row" style="justify-content: space-between">
      <experiment-create style="margin-left: 8px"></experiment-create>
      <div>
        <button
          v-if="canEditExperiments && hasLookerFolders"
          type="button"
          class="btn btn-sm btn-info"
          @click="$refs.templateLooksModal.openModal()"
          style="padding: 7px 7px; margin-right: 6px"
        >
          Set Template Looks
        </button>
        <button
          type="button"
          :class="`btn btn-sm ${filterBtnColors['hide']}`"
          @click="toggleHideFilter()"
          style="padding: 7px 7px"
        >
          Hiddens
        </button>
      </div>
    </div>
    <div class="row titles">
      <div class="col-md-1 col-12">Status</div>
      <div class="col-md-1 col-12">Exp. Id</div>
      <div
        class="col-md-1 col-12"
        @click="changeSortParams('name')"
        style="cursor: pointer"
      >
        {{
          "Category  " +
          (sortParams.field === "name"
            ? sortParams.direction === "ASC"
              ? "&#8595;"
              : "&#8593;"
            : "")
        }}
      </div>
      <div
        class="col-md-2 col-12"
        @click="changeSortParams('name')"
        style="cursor: pointer"
      >
        {{
          "Experiment Name  " +
          (sortParams.field === "name"
            ? sortParams.direction === "ASC"
              ? "&#8595;"
              : "&#8593;"
            : "")
        }}
      </div>
      <div class="col-md-2 col-12">Description</div>
      <div class="col-md-1 col-12">Looker</div>
      <div
        class="col-md-1 col-12"
        @click="changeSortParams('start_date')"
        style="cursor: pointer"
      >
        {{
          "Start  " +
          (sortParams.field === "start_date"
            ? sortParams.direction === "ASC"
              ? "&#8595;"
              : "&#8593;"
            : "")
        }}
      </div>
      <div
        class="col-md-1 col-12"
        @click="changeSortParams('end_date')"
        style="cursor: pointer"
      >
        {{
          "End  " +
          (sortParams.field === "end_date"
            ? sortParams.direction === "ASC"
              ? "&#8595;"
              : "&#8593;"
            : "")
        }}
      </div>
      <div
        class="col-md-1 col-12"
        @click="changeSortParams('pause_date')"
        style="cursor: pointer"
      >
        {{
          "Pause  " +
          (sortParams.field === "pause_date"
            ? sortParams.direction === "ASC"
              ? "&#8595;"
              : "&#8593;"
            : "")
        }}
      </div>
      <div class="col-md-1 col-12 text-center">Actions</div>
    </div>
    <div
      class="row mb-1 line-hover"
      v-for="experiment in filteredExperiments"
      :key="`experiment-${experiment.id}`"
    >
      <div class="col-md-1 col-12 center-para">
        <div :class="`status-box ${experiment.status}-${experiment.is_paused}`">
          {{ showExperimentStatus(experiment.status, experiment.is_paused) }}
        </div>
      </div>
      <div class="col-md-1 col-12 center-para" style=" white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
        <span  @click="copyExperimentId(experiment.id)">{{
          experiment.id
        }}</span>
      </div>
      <div class="col-md-1 col-12 center-para">
        {{ experiment.category || "-" }}
      </div>

      <div class="col-md-2 col-12 center-para">
        {{ experiment.name }}
      </div>
      <div
        class="col-md-2 col-12 center-para"
        @click="viewExperiment(experiment.id)"
      >
        <span>
          {{ experiment.description }}
        </span>
      </div>
      <div
        class="col-md-1 col-12 center-para" style=" white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
      >
        <span>
          <template v-if="isLookerLinkObject(experiment.looker_link)">
            <a href="#" @click.prevent="openLookerModal(experiment)">View Generated Looks</a>
          </template>
          <template v-else-if="experiment.looker_link">
            <a :href="experiment.looker_link" target="_blank" rel="noopener noreferrer">{{ experiment.looker_link }}</a>
          </template>
          <template v-else>-</template>
        </span>
      </div>
      <div class="col-md-1 col-12" @click="viewExperiment(experiment.id)">
        <template v-if="experiment.start_date">
          {{ experiment.start_date | moment("utc", "DD.MM.YYYY HH:mm") }} UTC
        </template>
        <template v-else>-</template>
      </div>
      <div class="col-md-1 col-12" @click="viewExperiment(experiment.id)">
        <template v-if="experiment.end_date">
          {{ experiment.end_date | moment("utc", "DD.MM.YYYY HH:mm") }} UTC
        </template>
        <template v-else>-</template>
      </div>
      <div class="col-md-1 col-12" @click="viewExperiment(experiment.id)">
        <template v-if="experiment.pause_date">
          {{ experiment.pause_date | moment("utc", "DD.MM.YYYY HH:mm") }} UTC
        </template>
        <template v-else>-</template>
      </div>
      <div class="col-md-1 col-12 text-center center-para">
        <popup-menu>
          <template v-slot:trigger>
            <i class="fas fa-ellipsis-v" />
          </template>
          <template v-slot:content>
            <div v-if="canEditThisExperiment(experiment)">
              <div class="text-left">
                <a @click="viewExperiment(experiment.id)">
                  <i class="fas fa-pen-square" /> Edit
                </a>
              </div>
            </div>
            <a
              v-if="experiment.status == 'started' && !experiment.is_paused"
              @click="changePauseStatus(experiment.id, true)"
              class="pause"
            >
              <i class="fas fa-stop-circle"></i>
              <span style="color: yellow">Pause Exp</span> &nbsp;
              <i
                class="far fa-question-circle"
                title="Pausing an experiment stops new users to be included in it"
              ></i>
            </a>
            <a
              v-if="experiment.status == 'started' && experiment.is_paused"
              @click="changePauseStatus(experiment.id, false)"
              style="color: #ffff99"
            >
              <i class="fas fa-stop-circle"></i> Continue Exp
            </a>
            <a
              v-if="experiment.status == 'started'"
              @click="changeStatus(experiment.id, 'ended')"
              style="color: red"
            >
              <i class="fas fa-stop-circle"></i> End Exp
            </a>
            <a
              v-if="experiment.status == 'created'"
              @click="changeStatus(experiment.id, 'started')"
            >
              <i class="fas fa-play"></i> Start Exp
            </a>
            <a @click="duplicateExperiment(experiment.id)">
              <i class="fas fa-copy" /> Duplicate
            </a>
            <div v-if="experiment.status == 'created'">
                <a
                  style="color: red"
                  
                  @click="deleteExperiment(experiment.id)"
                >
                  <i class="fas fa-ban" /> Delete
                </a>
              </div>
            <div v-else style="display: inline">
              <a
                @click="
                  $router.push({
                    name: 'Experiment',
                    params: { id: experiment.id, viewOnly: true },
                  })
                "
              >
                <i class="fas fa-eye" /> View
              </a>
            </div>
          </template>
        </popup-menu>
      </div>
    </div>
  </div>
</template>

<script>
import ExperimentCreate from "../components/ExperimentCreate";
import ExperimentStartChecklist from "../components/ExperimentStartChecklist";
import TemplateLooksModal from "../components/TemplateLooksModal.vue";
import LookerLinksModal from "../components/common/LookerLinksModal.vue";
import Spinner from "../components/Spinner.vue";
import { permissionsMixin } from '../mixins/permissions.mixin';
import { bucketService } from '../services/bucket.service';
import { isLookerLinkObject } from '../util/lookerLinkHelper';
import notify from '../util/notification';

export default {
  mixins: [permissionsMixin],
  data() {
    return {
      editList: [],
      sortParams: {
        field: "create_date",
        direction: "DESC",
      },
      showHiddenFilter: false,
      currentChecklistExperimentId: null,
      checklistImages: [],
      isLoading: false,
      loadingMessage: 'Processing...',
      selectedLookerLinks: null,
    };
  },
  computed: {
    experiments() {
      console.log(this.$store.getters.experiments)
      return this.$store.getters.experiments;
    },
    filteredExperiments() {
      const currentDate = new Date() * 1;
      const experiments = this.showHiddenFilter
        ? this.experiments
        : this.experiments.filter((item) => {
            const diff = currentDate - item.create_date;
            const statusFlag =
              item.status === "created" || item.status === "started" || item.status === "paused" ? true : false;
            //2 week control
            // return (!item.is_hidden && diff / 1209600 / 1000 < 1) || statusFlag;
            return statusFlag;
          });
      experiments.sort(this.sorter);
      return experiments;
    },
    filterBtnColors() {
      return {
        ios: this.iosFilter ? "btn-success" : "btn-secondary",
        android: this.androidFilter ? "btn-success" : "btn-secondary",
        hide: this.showHiddenFilter ? "btn-success" : "btn-secondary",
      };
    },
    hasLookerFolders() {
      const folders = this.$store.getters.lookerConfiguration.folders || {};
      return Object.keys(folders).length > 0;
    },
  },
  methods: {
    isLookerLinkObject,
    openLookerModal(experiment) {
      this.selectedLookerLinks = experiment.looker_link;
      this.$refs.lookerLinksModal.openModal();
    },
    showExperimentStatus(status, isPaused) {
      if (isPaused && status == "started") {
        return "paused";
      }
      return status;
    },
    async duplicateExperiment(id) {
      const experiment = this.experiment(id);
      const payload = {
        name: experiment.name + " Duplicate",
        description: experiment.description + " Duplicate",
        start_date: new Date(this.start_date) * 1,
        end_date: new Date(this.end_date) * 1,
        audience: experiment.audience,
        percentage: experiment.percentage,
        keys: experiment.keys,
        isls: experiment.isls,
        variants: experiment.variants,
        status: "created",
        create_date: Date.now(),
        only_new_users: experiment.only_new_users,
      };
      
      this.isLoading = true;
      this.loadingMessage = 'Duplicating experiment...';
      try {
        await this.$store.dispatch("configAdd", { type: "experiment", payload });
        notify('Experiment duplicated successfully!', 'success');
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.response?.data?.errorMessage || err.message || 'Unknown error';
        notify(errorMessage, 'error');
      } finally {
        this.isLoading = false;
      }
    },
    viewExperiment(id) {
      this.$router.push({ name: "Experiment", params: { id: id } });
    },
    experiment(id) {
      const experiment = this.filteredExperiments.find((item) => item.id == id);
      return experiment ? experiment : {};
    },
    async deleteExperiment(id) {
      if (
        confirm(
          `You are trying to delete "${
            this.experiment(id).name
          }" experiment. Are you sure?`
        )
      ) {
        this.isLoading = true;
        this.loadingMessage = 'Deleting experiment...';
        try {
          await this.$store.dispatch("configDelete", {
            type: "experiment",
            payload: id,
          });
          notify('Experiment deleted successfully!', 'success');
        } catch (err) {
          const errorMessage = err.response?.data?.message || err.response?.data?.errorMessage || err.message || 'Unknown error';
          notify(errorMessage, 'error');
        } finally {
          this.isLoading = false;
        }
      }
    },
    async changeStatus(id, status) {
      const experiment = this.experiments.find((el) => el.id == id);
      const isStaging = this.$store.getters.isStaging;
      
      // For starting experiments (created -> started) on production, use checklist modal
      // Skip image validation and checklist if skip_images is true
      if (experiment.status === 'created' && status === 'started' && !isStaging) {
        this.isLoading = true;
        this.loadingMessage = 'Validating experiment images...';
        
        // First validate that all variant images are uploaded
        const hasAllImages = await this.validateExperimentImages(experiment);
        if (!hasAllImages) {
          this.isLoading = false;
          return;
        }
        
        // Get images for checklist preview
        try {
          const response = await bucketService.getImages(experiment.id);
          this.checklistImages = response.data?.data || [];
        } catch (error) {
          this.checklistImages = [];
        }
        
        this.isLoading = false;
        
        // Open checklist modal
        this.currentChecklistExperimentId = id;
        this.$refs.experimentStartChecklist.openModal();
        return;
      }
      
      // For other status changes (end, staging start), use simple confirmation
      if (await this.checkSurvey(id, status)){
        const data = JSON.parse(JSON.stringify(experiment));
        delete data.id;
        data.status = status;
        if (status == "started") {
          data.start_date = Date.now();
        } else if (status == "ended") {
          data.end_date = Date.now();
        }
        
        this.isLoading = true;
        this.loadingMessage = status === 'ended' ? 'Ending experiment...' : 'Updating experiment...';
        
        try {
          await this.$store.dispatch("configEdit", {
            type: "experiment",
            payload: { id, data },
          });
          notify(`Experiment ${status === 'ended' ? 'ended' : 'updated'} successfully!`, 'success');
        } catch (err) {
          const errorMessage = err.response?.data?.message || err.response?.data?.errorMessage || err.message || 'Unknown error';
          notify(errorMessage, 'error');
        } finally {
          this.isLoading = false;
        }
      }
    },
    async validateExperimentImages(experiment) {
      // Skip validation if skip_images flag is set
      if (experiment.skip_images) {
        return true;
      }
      
      try {
        const response = await bucketService.getImages(experiment.id);
        const images = response.data?.data || [];
        
        const variantNames = experiment.variants.map(v => v._variant_name);
        const missingVariants = variantNames.filter(variantName => {
          return !images.some(img => img.variant === variantName);
        });
        
        if (missingVariants.length > 0) {
          notify(
            `Cannot start experiment: Missing UI images for variants: ${missingVariants.join(', ')}. Please upload images for all variants.`,
            'error'
          );
          return false;
        }
        
        return true;
      } catch (error) {
        console.error('Error validating experiment images:', error);
        notify('Error checking experiment images. Please try again.', 'error');
        return false;
      }
    },
    async handleChecklistConfirmed() {
      const id = this.currentChecklistExperimentId;
      const experiment = this.experiments.find((el) => el.id == id);
      const data = JSON.parse(JSON.stringify(experiment));
      delete data.id;
      data.status = 'started';
      data.start_date = Date.now();
      
      this.isLoading = true;
      this.loadingMessage = 'Starting experiment...';
      
      try {
        await this.$store.dispatch("configEdit", {
          type: "experiment",
          payload: { id, data },
        });
        notify('Experiment started successfully!', 'success');
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.response?.data?.errorMessage || err.message || 'Unknown error';
        notify(errorMessage, 'error');
      } finally {
        this.isLoading = false;
      }
      
      this.currentChecklistExperimentId = null;
      this.checklistImages = [];
    },
    handleChecklistCancelled() {
      this.currentChecklistExperimentId = null;
      this.checklistImages = [];
    },
    async checkSurvey(id, status) {
      const isStaging = this.$store.getters.isStaging
      const customConfirm = this.$confirm
      const customConfirmVariables = ['','question', {confirmButtonText: 'Yes', cancelButtonText: 'No'}]
      
      // For staging or non-start operations, use simple confirmation
      return await customConfirm(
        `You are trying to change status of "${
          this.experiment(id).name
        }" experiment. Are you sure?`,...customConfirmVariables
      ).catch(err => {return false})
    },
    async changePauseStatus(id, pauseStatus) {
      let change = pauseStatus ? "pause" : "continue";
      if (
        confirm(
          `You are trying to ${change} "${
            this.experiment(id).name
          }" experiment. Are you sure?`
        )
      ) {
        const experiment = this.experiments.find((el) => el.id == id);
        const data = JSON.parse(JSON.stringify(experiment));
        delete data.id;
        data["is_paused"] = pauseStatus;
        //latest pause date
        if (pauseStatus) {
          data["pause_date"] = Date.now();
        }
        
        this.isLoading = true;
        this.loadingMessage = pauseStatus ? 'Pausing experiment...' : 'Continuing experiment...';
        
        try {
          await this.$store.dispatch("configEdit", {
            type: "experiment",
            payload: { id, data },
          });
          notify(`Experiment ${pauseStatus ? 'paused' : 'continued'} successfully!`, 'success');
        } catch (err) {
          const errorMessage = err.response?.data?.message || err.response?.data?.errorMessage || err.message || 'Unknown error';
          notify(errorMessage, 'error');
        } finally {
          this.isLoading = false;
        }
      }
    },
    copyExperimentId(eid) {
      const tempInput = document.createElement("input");
      tempInput.value = eid;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
    },
    changeSortParams(field) {
      if (this.sortParams.field === field) {
        this.sortParams.direction =
          this.sortParams.direction === "ASC" ? "DESC" : "ASC";
      } else {
        this.sortParams = { field, direction: "ASC" };
      }
    },
    sorter(a, b) {
      const aField = a[this.sortParams.field] || 0;
      const bField = b[this.sortParams.field] || 0;
      if (typeof aField === "string" && typeof bField === "string") {
        if (aField.toUpperCase() < bField.toUpperCase())
          return this.sortParams.direction === "ASC" ? -1 : 1;
        if (aField.toUpperCase() > bField.toUpperCase())
          return this.sortParams.direction === "ASC" ? 1 : -1;
        return 0;
      }
      if (aField < bField) return this.sortParams.direction === "ASC" ? -1 : 1;
      if (aField > bField) return this.sortParams.direction === "ASC" ? 1 : -1;
      return 0;
    },
    toggleHideFilter() {
      this.showHiddenFilter = !this.showHiddenFilter;
    },
  },
  components: {
    ExperimentCreate,
    ExperimentStartChecklist,
    TemplateLooksModal,
    LookerLinksModal,
    Spinner,
  },
  filters: {
    addUTCAfter(value) {
      if (value) {
        return value + " UTC";
      } else {
        return "-";
      }
    },
  },
};
</script>

<style scoped>
.status-box {
  text-align: center;
  border-radius: 5px;
  padding: 5px;
}

[class*="created"] {
  background-color: #95a5a6;
}

.status-box.started-undefined {
  background-color: #27ae60;
}

.status-box.started-true {
  background-color: #ffff99;
  color: black;
}
.status-box.started-false {
  background-color: #27ae60;
}
[class*="ended"] {
  background-color: #e74c3c;
}

.popup {
  color: white;
  margin: 5px 0;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-overlay p {
  color: #fff;
  margin-top: 1rem;
  font-size: 1.1rem;
}
</style>